import { collection, doc, getDoc, getDocs, setDoc, deleteDoc, query, where, writeBatch } from "firebase/firestore";
import { SortableOption } from "@/types/fieldsOptions";
import { Firestore } from "firebase/firestore";
import cacheService from "./cacheService";

export const getSortableOptions = async (db: Firestore): Promise<SortableOption[]> => {
  try {
    // Check cache first
    const cacheKey = "sortableOptions_all";
    const cachedOptions = cacheService.get<SortableOption[]>(cacheKey);
    
    if (cachedOptions) {
      console.log("Using cached sortable options");
      return cachedOptions;
    }
    
    const sortableOptionsRef = collection(db, "sortableOptions");
    const snapshot = await getDocs(sortableOptionsRef);
    
    const options = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    } as SortableOption));
    
    // Cache the results
    cacheService.set(cacheKey, options);
    
    return options;
  } catch (error) {
    console.error("Error fetching sortable options:", error);
    throw error;
  }
};

export const getSortableOptionById = async (db: Firestore, id: string): Promise<SortableOption | null> => {
  try {
    // Check if we have all options cached
    const cacheKey = "sortableOptions_all";
    const cachedOptions = cacheService.get<SortableOption[]>(cacheKey);
    
    if (cachedOptions) {
      const option = cachedOptions.find(o => o.id === id);
      if (option) {
        return option;
      }
    }
    
    const docRef = doc(db, "sortableOptions", id);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      return { id: docSnap.id, ...docSnap.data() } as SortableOption;
    }
    
    return null;
  } catch (error) {
    console.error(`Error fetching sortable option with ID ${id}:`, error);
    throw error;
  }
};

// The following functions should clear the cache when they modify sortable options
export const createSortableOption = async (db: Firestore, data: Omit<SortableOption, "id">, customId?: string): Promise<SortableOption> => {
  try {
    let docRef;
    if (customId) {
      docRef = doc(db, "sortableOptions", customId);
    } else {
      docRef = doc(collection(db, "sortableOptions"));
    }
    
    await setDoc(docRef, data);
    
    // Clear related caches
    cacheService.clear("sortableOptions_all");
    cacheService.clear(`sortableOptions_${data.type}`);
    
    return {
      id: docRef.id,
      ...data
    };
  } catch (error) {
    console.error("Error creating sortable option:", error);
    throw error;
  }
};

export const updateSortableOption = async (db: Firestore, id: string, data: Partial<Omit<SortableOption, "id">>): Promise<void> => {
  try {
    const docRef = doc(db, "sortableOptions", id);
    await setDoc(docRef, data, { merge: true });
    
    // Clear all related caches
    cacheService.clear("sortableOptions_all");
    if (data.type) {
      cacheService.clear(`sortableOptions_${data.type}`);
    } else {
      // Get the current option to know its type
      const currentOption = await getDoc(docRef);
      if (currentOption.exists()) {
        const type = currentOption.data().type;
        cacheService.clear(`sortableOptions_${type}`);
      }
    }
    
  } catch (error) {
    console.error(`Error updating sortable option with ID ${id}:`, error);
    throw error;
  }
};

export const deleteSortableOption = async (db: Firestore, id: string): Promise<void> => {
  try {
    // Get the option first to know its type
    const docRef = doc(db, "sortableOptions", id);
    const currentOption = await getDoc(docRef);
    
    await deleteDoc(docRef);
    
    // Clear caches
    cacheService.clear("sortableOptions_all");
    if (currentOption.exists()) {
      const type = currentOption.data().type;
      cacheService.clear(`sortableOptions_${type}`);
    }
    
  } catch (error) {
    console.error(`Error deleting sortable option with ID ${id}:`, error);
    throw error;
  }
};

export const deleteSortableOptionsBulk = async (db: Firestore, ids: string[]): Promise<void> => {
  try {
    // Get all types of options being deleted
    const typesToClear = new Set<string>();
    const batch = writeBatch(db);
    
    // First get all options to know their types
    for (const id of ids) {
      const docRef = doc(db, "sortableOptions", id);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        typesToClear.add(docSnap.data().type);
      }
      batch.delete(docRef);
    }
    
    await batch.commit();
    
    // Clear caches
    cacheService.clear("sortableOptions_all");
    typesToClear.forEach(type => {
      cacheService.clear(`sortableOptions_${type}`);
    });
    
  } catch (error) {
    console.error(`Error bulk deleting sortable options:`, error);
    throw error;
  }
};

/**
 * Bootstraps `sortableOptions` on an EMPTY collection only — it returns early
 * once any doc exists, so editing `initialData` does not change an environment
 * that has already been seeded. Prod proves it: prod carries `e_appOpen` and
 * `e_appDue`, which this literal has never listed. (Prod's `e_formsDue` label
 * also read "Camp forms due" against this literal's "Camp forms dues" until
 * KB-2709 corrected the typo here — the divergence was invisible for exactly
 * as long as nobody compared the two.)
 *
 * Each surface reads a DIFFERENT copy of this vocabulary, so a new key must be
 * added to whichever ones should surface it:
 *   - pro's admin controls read the live Firestore collection;
 *   - kb-ff makes no Firestore read at all — it renders from a compile-time
 *     catalog it ships (`bundled_filter_options.g.dart`, generated from
 *     `kb-ff:tool/fixtures/filter_options_snapshot.json`).
 * Adding a key here alone reaches neither of them. (KB-2709)
 */
export const initializeSortableOptions = async (db: Firestore): Promise<void> => {
  try {
    const snapshot = await getDocs(collection(db, "sortableOptions"));
    
    if (!snapshot.empty) {
      console.log("Sortable options already exist. Skipping initialization.");
      return;
    }
    
    const initialData = [
      ["t_creative", "theme", "Creative", "Creative", 1],
      ["t_outdoors", "theme", "Outdoors", "Outdoors", 2],
      ["t_sports", "theme", "Sports", "Sports", 3],
      ["t_stem", "theme", "STEM", "STEM", 4],
      ["t_personalDev", "theme", "Personal growth", "Personal growth", 5],
      ["s_canAccommodate", "supportFor", "Accommodation possible", "Camp program can accommodate campers with disabilities", 1],
      ["s_trained", "supportFor", "Accommodation-trained", "Camp staff receive training on accommodating campers with disabilities", 2],
      ["s_specialCondition", "supportFor", "Specializes in condition(s)", "Camp specializes in one or more conditions", 3],
      ["s_oneOnOne", "supportFor", "1-on-1 support", "Camp can provide 1-on-1 support", 4],
      ["s_canHelpToilet", "supportFor", "Can assist with toileting/hygiene", "Campers do not have to be independent with toileting and hygiene", 5],
      ["a_nutFree", "allergyFriendly", "Nut-free", "Nut-free", 1],
      ["a_canAccommodate", "allergyFriendly", "Allergy accommodation", "Can accommodate allergies", 2],
      ["r_none", "religiousOptions", "No religious affiliation", "No religious affiliation", 1],
      ["r_christian", "religiousOptions", "Christian", "Christian", 2],
      ["r_jewish", "religiousOptions", "Jewish", "Jewish", 3],
      ["r_other", "religiousOptions", "Other", "Other", 4],
      ["o_summer", "offering", "Summer camp", "Summer camp", 1],
      ["o_spring", "offering", "Spring camp", "Spring camp", 2],
      ["o_fall", "offering", "Fall camp", "Fall camp", 3],
      ["o_schoolsOut", "offering", "Schools-out camp", "Schools-out camp", 4],
      ["o_trackOut", "offering", "Track out camp", "Track out camp", 5],
      ["o_afterSchool", "offering", "After-school", "After-school", 6],
      ["o_fullDay", "offering", "Full day", "Full day", 7],
      ["o_halfDayMorning", "offering", "Half day (morning)", "Half day (morning)", 8],
      ["o_halfDayAfternoon", "offering", "Half day (afternoon)", "Half day (afternoon)", 9],
      ["o_overnight", "offering", "Overnight", "Overnight", 10],
      ["o_cit", "offering", "CIT", "CIT", 11],
      ["e_posted", "registrationEvents", "Calendar gets posted", "Calendar gets posted", 1],
      ["e_openLimited", "registrationEvents", "Registration opens to priority audience", "Registration opens to priority audience", 2],
      ["e_open", "registrationEvents", "Registration opens to general public", "Registration opens to general public", 3],
      ["e_closes", "registrationEvents", "Registration closes", "Registration closes", 4],
      ["e_closesOnline", "registrationEvents", "Online registration closes", "Online registration closes", 4],
      ["e_discountEnds", "registrationEvents", "Early bird discount ends", "Early bird discount ends", 5],
      ["e_decisions", "registrationEvents", "Decisions communicated", "Decisions communicated", 6],
      ["e_depositDue", "registrationEvents", "Deposit due", "Deposit due", 7],
      ["e_formsDue", "registrationEvents", "Camp forms due", "Camp forms due", 8],
      ["c_coed", "coed", "Co-ed", "Co-ed", 1],
      ["c_girl", "coed", "Girl-only", "Girl-only", 2],
      ["c_boy", "coed", "Boy-only", "Boy-only", 3],
      // These ten status rows MIRROR functions/src/shared/offer/offerStatusEnums.ts, the one TS source of
      // both value lists. They stay literal because the camp-data pipeline's
      // canonicalStatusEnums.cjs parses them from this file. offerStatusEnums.drift.test.ts pins the two equal.
      ["su_notYet", "signUpStatus", "Not yet open", "Not yet open", 1],
      ["su_openLimited", "signUpStatus", "Open to priority audience", "Open to priority audience", 2],
      ["su_open", "signUpStatus", "Open to general public", "Open to general public", 3],
      ["su_closed", "signUpStatus", "Closed", "Closed", 4],
      ["su_unknown", "signUpStatus", "Unknown", "Unknown", 5],
      ["av_space", "availStatus", "Has availability", "Has availability", 1],
      ["av_limited", "availStatus", "Limited availability", "Limited availability", 2],
      ["av_waitlist", "availStatus", "Waitlist only", "Waitlist only", 3],
      ["av_fullNoWaitlist", "availStatus", "No availability", "No availability", 4],
      ["av_unknown", "availStatus", "Unknown", "Unknown", 5],
      ["v_published", "visibilityStatus", "Published", "Published", 1],
      ["v_draft", "visibilityStatus", "Draft", "Draft", 2],
      ["v_acrhived", "visibilityStatus", "Archived", "Archived", 3],
      ["v_kbOnly", "visibilityStatus", "KB Only", "KB Only", 4],
      ["pt_1", "priceTier", "$", "$", 1],
      ["pt_2", "priceTier", "$$", "$$", 2],
      ["pt_3", "priceTier", "$$$", "$$$", 3],
      ["pt_scholarships", "priceTier", "Offers scholarships", "Offers scholarships", 4],
      // Workflow status options
      ["wf_unknown", "workflow", "Unknown", "Unknown workflow status", 1],
      ["wf_new", "workflow", "Just started", "Started", 2],
      ["wf_internal1", "workflow", "Ready for Anne", "Ready for Anne", 3],
      ["wf_internal2", "workflow", "Back to Nico", "Back to Nico", 4],
      ["wf_external1", "workflow", "Ready to send to camp", "Ready for camp", 5],
      ["wf_external2", "workflow", "Sent to camp", "Sent to camp", 6],
      ["wf_external3", "workflow", "Camp engaged", "Camp engaged", 7]
    ];
    
    const batch = writeBatch(db);
    
    initialData.forEach(([docId, type, shortLabel, label, sortOrder]) => {
      const docRef = doc(db, "sortableOptions", String(docId));
      
      batch.set(docRef, {
        type,
        label,
        shortLabel,
        sortOrder: Number(sortOrder)
      });
    });
    
    await batch.commit();
    console.log("Sortable options initialized successfully.");
  } catch (error) {
    console.error("Error initializing sortable options:", error);
    throw error;
  }
};
